import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { PrivateBillCostCenter } from "@/Common/Components/Billing/PrivateBillCostCenter";
import { TimeSelector } from "@/Common/Components/Inputs/TimeSelector";
import { TimeSpan } from "@/Common/Models/TimeSpan";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { TimerComponent_SlaveLongPool } from "@/Patient/Components/MedicalNote/TimerComponent_SlaveLongPool";
import { SummaryPanelButtons } from "@/Patient/Components/MedicalSummaryPanel/SummaryPanelButtons";
import { SentFaxInfo } from "@/Patient/Models/Fax/SentFaxInfo";
import { Encounter, EncounterRelatedEmployee } from "@/Patient/Models/MedicalSummary/Encounter";
import { IPatientUIModel } from "@/Patient/Models/UI/IPatientUIModel";
import { MedicalNoteViewMode } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalNote";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { SentFaxInfoBox } from "./SentFaxStatus/SentFaxInfoBox";

import "./MedicalNoteHeader.scss";

import LinkedAppointment from "@/Globals/_images/linked-appointment-icon.svg";
import UnLinkedAppointment from "@/Globals/_images/unlinked-appointment-icon.svg";

interface IMedicalNoteHeaderProps {
    encounter: Encounter;
    isReadOnly?: boolean;
    patientUIModel?: IPatientUIModel;
    noteModel?: IPatientMedicalNoteModel;
    isMedicalSummaryPanelDisplayed?: boolean;
    employeeDurationEdit?: boolean;
    employeeDurationEditValue?: TimeSpan;
    onEditConsultationDetailsClick?: (consultationContextLookupId?: string) => void;
    onMedicalSummaryLinkClick?: () => void;
    onOpenSecondScreenClick?: () => void;
    onPrintMedicalNote?: (e: React.MouseEvent) => void;
    onShowMedicalNoteFaxPopup?: (e: React.MouseEvent) => void;
    onTimerChangeCallback?: (value: Date) => void;
    onTimerEnterCallBack?: (value: Date) => void;
    onTimerEscapeCallBack?: () => void;
    onTimerClick?: () => void;
}

export function MedicalNoteHeader(props: IMedicalNoteHeaderProps): JSX.Element {
    const specialityTitle = cacheStore.GetLocalizedValueByLookupId(props.encounter.ConsultationTypeLookupId);
    const consultationContext = cacheStore.GetLocalizedValueByLookupId(props.encounter.ConsultationContextLookupId);
    const encounterDateString = LuxonDateTimeParser.parse(props.encounter.ConsultationDate).toFormat("yyyy-MM-dd");
    const clinicalName = cacheStore.GetSiteById(props.encounter.SiteId)?.Model?.ClinicalName ?? "";
    const privateBillCost = PrivateBillCostCenter.GetDisplayName(cacheStore.GetCostCenterById(props.encounter.CostCenterId));
    const hasAutoApproveNotice = cacheStore.ClinicSettings.EnableDirectPatientProfileEdits && props.encounter.AutoApprove || props.encounter.IsImplicitlyApproved;
    const showSentFaxIcon =
        cacheStore.ClinicSettings.EnableFaxSendingIndicator
        && props.encounter.IsSignedOff
        && props.noteModel.get("ViewMode") !== MedicalNoteViewMode.Edit;

    let signedEmployeeName = "";
    if (props.encounter.SignedOffByEmployeeId) {
        const signedEmployee = cacheStore.GetEmployeeByContactFacetId(props.encounter.SignedOffByEmployeeId);
        if (signedEmployee) {
            const employeePrefix = cacheStore.GetLocalizedValueByLookupId(signedEmployee.PrefixLookupId);
            signedEmployeeName = employeePrefix
                ? `${employeePrefix} ${signedEmployee.LastName}, ${signedEmployee.FirstName}`
                : `${signedEmployee.LastName}, ${signedEmployee.FirstName}`;
        }
    }

    const showConsultationContext = props.encounter.IsSignedOff;
    const sentFaxes = props.noteModel.get("SentFaxes") ?? {};
    const medicalNoteFaxes = sentFaxes[props.encounter.Id];

    return (
        <div className={"note-block flex-column medical-note-header-container" + (!props.encounter.IsSignedOff ? " unapproved-note" : "")}>
            <div className="display-flex-with-space-between medical-note-header">
                <div className="margin-xs-left">
                    <div data-cy="medicalNote-consultationDetails" className="flex-container medical-note-header-encounter-consultation">
                        <div className="display-inline-flex">
                            <span className="medical-note-header-encounter-date ellipsis-auto">{encounterDateString}</span>
                        </div>
                        <div className="display-inline-flex">
                            <span className="push-left-5 ellipsis-auto" title={specialityTitle} style={{ maxWidth: "250px" }}>
                                {specialityTitle}
                            </span>
                        </div>
                        <div className="medical-note-header-encounter-consultation-grid normal-text">
                            <span className="margin-xs-left">(</span>
                            {showConsultationContext && (
                                <span className="medical-note-header-encounter-consultation-context ellipsis-auto" title={consultationContext}>
                                    {consultationContext}
                                </span>
                            )}
                            {(cacheStore.IsMultiSite() && props.encounter && props.encounter.SiteId) && (
                                <>
                                    <span className={`medical-note-header-encounter-clinical-name ${showConsultationContext ? "margin-xs-left" : ""} ellipsis-auto`} title={clinicalName}>
                                        {(showConsultationContext ? "- " : "") + clinicalName}
                                    </span>
                                    {!IsNullOrWhiteSpace(privateBillCost) &&
                                        (
                                            <span className="medical-note-header-encounter-private-bill-cost margin-xs-left ellipsis-auto" title={privateBillCost}>
                                                {"- " + privateBillCost}
                                            </span>
                                        )
                                    }

                                </>
                            )}
                            <span className="margin-xs-right">)</span>
                        </div>
                        {cacheStore.ClinicSettings.EnableAutoLinkAppointmentToNote && (
                            <div
                                data-cy={props.noteModel.get("Encounter").AppointmentId
                                    || props.noteModel.get("Encounter").SuggestedAppointmentLinkId ? "appointment-linked" : "appointment-not-linked"
                                }
                            >
                                {(props.noteModel.get("Encounter").AppointmentId
                                    || props.noteModel.get("Encounter").SuggestedAppointmentLinkId) ?
                                        <LinkedAppointment /> : <UnLinkedAppointment />
                                }
                            </div>
                        )}
                        {!props.isReadOnly && (
                            <div className="normal-text white-space-no-wrap">
                                <span className="margin-xs-left margin-xs-right">-</span>
                                <span>
                                    <a data-cy="medicalNote-editConsultationDetails" onClick={() => props.onEditConsultationDetailsClick?.()}>
                                        {cacheStore.GetString("EDITLabel")}
                                    </a>
                                </span>
                            </div>
                        )}
                    </div>
                    <div data-cy="medicalNote-relatedEmployees">
                        {props.encounter.EncounterRelatedEmployees.sortByProperty(x => x.FirstContribution).map((x: EncounterRelatedEmployee) => {
                            const emp = cacheStore.GetEmployeeByContactFacetIdDictionary(x.EmployeeId);
                            let start = "";
                            let startDateString = "";
                            if (x.FirstContribution) {
                                const startDate = LuxonDateTimeParser.parse(x.FirstContribution);
                                startDateString = startDate.toFormat("yyyy-MM-dd");
                                start = startDate?.toFormat(startDateString == encounterDateString ? "HH:mm" : "yyyy-MM-dd HH:mm");
                            }

                            let end = "";
                            if (x.LastContribution) {
                                const endDate = LuxonDateTimeParser.parse(x.LastContribution);
                                const endDateString = endDate.toFormat("yyyy-MM-dd");
                                end = endDate?.toFormat((endDateString == startDateString || endDateString == encounterDateString) ? "HH:mm" : "yyyy-MM-dd HH:mm");
                            }

                            const durationTickNumber = Number(x.DurationTicks);
                            const time = !isNaN(durationTickNumber) && durationTickNumber > 0
                                ? TimeSpan.FromTicks(Number(x.DurationTicks))
                                : new TimeSpan();

                            let hasTimer = false;
                            if (props.patientUIModel?.get("EditEncounterIdContext") == props.encounter.Id && x.EmployeeId == sessionStore.getCurrentEmployee().ContactFacetId) {
                                hasTimer = true;
                            }

                            let isEditTimer = false;
                            if (props.employeeDurationEdit && x.EmployeeId == sessionStore.getCurrentEmployee().ContactFacetId)
                                isEditTimer = true;

                            return (
                                <div key={x.EncounterId + x.EmployeeId} className="flex-container" style={{ justifyContent: "flex-start" }}>
                                    <div style={{ minWidth: "32px" }}>
                                        <span>{start}</span>
                                    </div>
                                    {!cacheStore.GetCurrentEmployee().DisableTimers && (
                                        <div style={{ marginLeft: "3px" }}>
                                            <span>-</span>
                                        </div>
                                    )}
                                    {!sessionStore.getCurrentEmployee().DisableTimers && (
                                        <div style={{ minWidth: "32px", marginLeft: "3px" }}>
                                            <span>{end}</span>
                                        </div>
                                    )}
                                    <div style={{ marginLeft: "11px" }}>
                                        <strong>{emp?.ContactFacet_id ? Employee.getFullNameWithPrefixNameSuffixTitleForEmployee(emp, true) : x.EmployeeId}</strong>
                                    </div>
                                    {!sessionStore.getCurrentEmployee().DisableTimers && !props.isReadOnly && (
                                        <div>
                                            {isEditTimer && (
                                                <div className="pointer" style={{ marginLeft: "5px", color: "#0070d2" }} onClick={e => e.stopPropagation()}>
                                                    <div className="flex-container" style={{ justifyContent: "center" }}>
                                                        <TimeSelector
                                                            id="time_001_encounterDurationForEmployee"
                                                            onChangeCallback={value => props.onTimerChangeCallback(value)}
                                                            date={new Date(0, 0, 0, 0, 0, Number(props.employeeDurationEditValue.TotalSeconds()))}
                                                            dateFormat="HH:mm:ss"
                                                            disabled={false}
                                                            interval={5}
                                                            max={new Date(0, 0, 0, 24)}
                                                            width={110}
                                                            height={33}
                                                            smallHeight={true}
                                                            focusOnMount={true}
                                                            onEnterCallBack={value => props.onTimerEnterCallBack(value)}
                                                            onEscapeCallBack={() => props.onTimerEscapeCallBack()}
                                                            isRequired
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            {!isEditTimer && (
                                                <div
                                                    className="pointer"
                                                    style={{ marginLeft: "5px", color: "#0070d2" }}
                                                    onClick={() => props.onTimerClick()}
                                                >
                                                    {!hasTimer && time.ticks !== 0 &&
                                                        <span>{time.toString("(HH:mm:ss)")}</span>
                                                    }
                                                    {hasTimer && (
                                                        <TimerComponent_SlaveLongPool
                                                            format="(HH:mm:ss)"
                                                            resetId={props.encounter.Id}
                                                            getValue={() => {
                                                                if (sessionStore.timerComponentModel != null && sessionStore.timerComponentModel.encounterId == props.encounter.Id)
                                                                    return sessionStore.timerCurrentValue;
                                                                else
                                                                    return time;
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className={(props.isReadOnly ? "" : "display-flex-row")}>
                    {showSentFaxIcon && medicalNoteFaxes?.any() && (
                        <div className="medical-note-header-fax-indicator">
                            <SentFaxInfoBox
                                data={medicalNoteFaxes.map((fax: SentFaxInfo) => ({
                                    sentFaxItemId: fax.SentFaxItemId,
                                    sentBy: fax.SenderEmployeeName,
                                    sentDate: fax.SentDate,
                                    faxStatus: fax.SentStatus,
                                    recipient: fax.RecipientName,
                                    receivedDate: fax.ReceiveDate,
                                    recipientFax: fax.RecipientFax,
                                    faxId: fax.Id,
                                    itemType: fax.SentFaxItemTypeId,
                                    senderEmployeeId: fax.SenderEmployeeId
                                }))}
                                tooltipId={`react-tooltip-${props.encounter.Id}`}
                                iconButtonSize="sm"
                                parentElementId="medical-note-container"
                            />
                        </div>
                    )}
                    <div className="right-medical-note-header">
                        {props.encounter.IsSignedOff && (
                            <div data-cy="medicalNote-signedoff">
                                <div>
                                    <strong>
                                        {hasAutoApproveNotice ?
                                            cacheStore.GetString("SignedOffAutomaticallyByLabel") :
                                            cacheStore.GetString("SignedOffByLabel")
                                        } {signedEmployeeName}
                                    </strong>
                                </div>
                                <div>
                                    <span className="text-gray"> {LuxonDateTimeParser.parse(props.encounter.SignedOffDate)?.toFormat("yyyy-MM-dd hh:mm a")} </span>
                                    {!props.isReadOnly && (
                                        <div style={{ display: "inline" }}>
                                            {" - "}<a onClick={e => props.onPrintMedicalNote(e)}>{cacheStore.GetString("PrintLabel")}</a>
                                            {sessionStore.signOnPrintAndFaxAccess() && (
                                                <span> / <a onClick={e => props.onShowMedicalNoteFaxPopup(e)}>{cacheStore.GetString("FaxVerbLabel")}</a></span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {!props.encounter.IsSignedOff && props.isReadOnly && (
                            <div className="unapproved-label">
                                {cacheStore.GetString("UnapprovedNoteLabel")}
                            </div>
                        )}
                        {props.noteModel?.get("SupervisorName") != "" && !props.encounter?.IsSignedOff && (
                            <div className="pull-right" style={{ "marginRight": "10px" }}>
                                <div>
                                    <strong>{props.noteModel?.get("PendingApprovalStatusInt") === 0 ? cacheStore.GetString("PendingApprovalLabel") : cacheStore.GetString("ReturnedByLabel")} {props?.noteModel.get("SupervisorName")}</strong>
                                </div>
                            </div>
                        )}
                    </div>
                    {cacheStore.ClinicSettings.EnableSecondScreen && !props.isReadOnly && (
                        <SummaryPanelButtons
                            isMedicalSummaryPanelOpen={props.isMedicalSummaryPanelDisplayed}
                            hideOpenSummaryPanelLink={true}
                            onMedicalSummaryLinkClick={() => props.onMedicalSummaryLinkClick?.()}
                            onPopoutClick={() => props.onOpenSecondScreenClick?.()}
                            cssClass="noteHeaderToggleContainer"
                            encounter={props.encounter}
                            onEditConsultationDetailsClick={props.onEditConsultationDetailsClick}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
